import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { g, p, eur, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Users } from "lucide-react";

export default function Direttore() {
  const { t: tr } = useI18n();
  const { departments, user } = useApp();
  const [tables, setTables] = useState([]);
  const nav = useNavigate();

  const load = useCallback(() => g("/tables").then(setTables), []);
  useEffect(() => {
    load();
    const i = setInterval(load, 12000);
    return () => clearInterval(i);
  }, [load]);

  const open = tables.filter((t) => t.order);
  const totOpen = open.reduce((s, t) => s + t.order.total, 0);
  const totRem = open.reduce((s, t) => s + t.order.remaining, 0);

  const via = async (orderId, department) => {
    try {
      const r = await p(`/orders/${orderId}/send`, { department, by: user.name });
      toast.success(tr("d.sentOk", { n: r.sent, depts: r.departments.join(", ") }));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const seen = new Set();
  const orders = open.filter((t) => {
    if (seen.has(t.order.id)) return false;
    seen.add(t.order.id);
    return true;
  });

  return (
    <div className="fade-up space-y-5" data-testid="direttore-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{tr("d.overline")}</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">{tr("d.title")}</h1>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[[tr("d.open"), orders.length], [tr("d.value"), eur(totOpen)], [tr("d.toCash"), eur(totRem)]].map(([l, v]) => (
          <Card key={l} className="p-5" data-testid={`dir-kpi-${l.replace(/\s+/g, "-").toLowerCase()}`}>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{l}</p>
            <p className="font-num mt-3 text-2xl font-bold">{v}</p>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {orders.map((t) => {
          const w = t.order.waiting_minutes;
          return (
            <Card
              key={t.order.id}
              className={`border-2 p-5 ${w >= 20 ? "animate-pulse border-rose-500 bg-rose-50" : w >= 10 ? "animate-pulse border-amber-500 bg-amber-50" : "border-slate-200"}`}
              data-testid={`dir-order-${t.order.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-head text-xl font-bold">{t.order.group_name || `Tavolo ${t.number}`}</p>
                  <p className="text-xs text-slate-600">
                    {t.order.waiter} · <Users className="inline h-3 w-3" /> {t.order.covers} {tr("s.covers")}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-num text-lg font-bold">{eur(t.order.total)}</p>
                  <p className="text-xs text-slate-500">{tr("d.due")} {eur(t.order.remaining)}</p>
                </div>
              </div>
              {w > 0 && (
                <p className="mt-2 flex items-center gap-1 text-xs font-semibold text-rose-700">
                  <Clock className="h-3.5 w-3.5" /> {tr("d.waiting", { n: w })}
                </p>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" data-testid={`dir-via-all-${t.order.id}`} onClick={() => via(t.order.id, null)}>
                  {tr("d.viaAll")}
                </Button>
                {departments.map((d) => (
                  <Button key={d} size="sm" variant="outline" data-testid={`dir-via-${d}-${t.order.id}`} onClick={() => via(t.order.id, d)}>
                    {tr("c.onlyDept", { d })}
                  </Button>
                ))}
                <Button size="sm" variant="ghost" data-testid={`dir-open-${t.order.id}`} onClick={() => nav(`/comanda/${t.order.id}`)}>
                  {tr("s.openOrder")}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
